import { defineStore } from 'pinia'
import { api } from '@/api'
import { getEnv } from '@env'
import { groupBy } from '../utils/groupBy' // helper function to group objects
import router from '../router'
import i18n from '../i18n'
import { setTimeZone } from '../utils/countryConfig'
import moment from 'moment'

export const useBookingStore = defineStore('booking', {
  state: () => ({
    fail: false,
    services: {},
    getServiceLoading: false,
    appCountry: getEnv('VUE_APP_FRONTEND_COUNTRY'),
    groupValue: '',
    appLang: '',
    appointmentLang: '',
    appointmentType: '',
    appointmentTypeFromUrl: '',
    getSubUnitsLoading: false,
    subUnits: {},
    groupedSubUnits: {},
    showBranchSelect: false,
    branchType: '',
    getTimesLoading: false,
    availableDates: {
      dates: [],
    },
    getAvailableDatesLoading: false,
    times: [],
    selectedBranch: '',
    bookingErrors: [],
    selectedDate: null,
    selectedTime: '',
    comment: '',
    appointmentData: '',
    appointmentDataLoading: false,
    missingAppointmentKey: false,
    cancelAppointmentData: {},
    cancelAppointmentDataLoading: false,
    cancelAppointment: {},
    cancelAppointmentLoading: false,
    closestBranchArray: [],
    sessionId: '',
    errorMessage: '',
    personalViewAllowed: false,
    meetingDuration: false,
    selectedServiceObject: null,
    childrenCount: null,
    childInputs: false,
    childrenArray: [],
    representativeData: {},
    disabledDates: {
      to: '',
      from: '',
      dates: [],
    },
  }),

  actions: {
    setAppCountry(value) {
      this.appCountry = value
    },
    setCustomerType(value) {
      this.groupValue = value
    },
    setAppointmentLang(value) {
      this.appointmentLang = value
    },
    setAppLang(value) {
      this.appLang = value
      i18n.global.locale = value
    },
    setAppointmentType(value) {
      this.appointmentType = value
      if (value !== '') {
        this.showBranchSelect = true //allow branch selection
      }
    },
    setAppointmentTypeFromUrl(value) {
      this.appointmentTypeFromUrl = value
    },
    toggleBranchSelect(value) {
      this.showBranchSelect = value
    },
    clearSubUnits() {
      this.subUnits = {}
      this.groupedSubUnits = {}
    },
    setBranchType(value) {
      this.branchType = value
    },
    setSelectedBranch(value) {
      this.selectedBranch = value
    },
    deleteTimes() {
      this.times = []
    },
    setErrors(value) {
      this.bookingErrors.push(value)
    },
    clearErrors() {
      this.bookingErrors = []
    },
    setSelectedDate(value) {
      this.selectedDate = value
    },
    setSelectedTime(value) {
      this.selectedTime = value
    },
    setComment(value) {
      this.comment = value
    },
    updateClosestBranchArray(value) {
      this.closestBranchArray = value
    },
    setSessionId(value) {
      this.sessionId = value
    },
    resetFailState(value) {
      this.fail = value
    },
    allowPersonalView(value) {
      this.personalViewAllowed = value
    },
    clearMeetingDuration() {
      this.meetingDuration = false
    },
    setSelectedServiceObject(value) {
      this.selectedServiceObject = value
    },
    setChildrenCount(value) {
      this.childrenCount = value
    },
    setChildInputs(value) {
      this.childInputs = value
    },
    setChildrenArray(value) {
      this.childrenArray = value
    },
    setRepresentativeData(value) {
      this.representativeData = value
    },
    async getServices({ groupValue, lang, country, first, subUnitId, meetingLang }) {
      this.fail = false
      this.getServiceLoading = true
      try {
        const response = await api.getServices(groupValue, lang, country, first, subUnitId, meetingLang)
        this.services = response.data.services
        this.getServiceLoading = false
      } catch (error) {
        this.fail = error
        this.getServiceLoading = false
      }
    },

    async getSubUnits({ groupValue, lang, country, first, appointmentTtypeId, meetingLang }) {
      this.fail = false
      this.getSubUnitsLoading = true
      try {
        const response = await api.getSubUnits(groupValue, lang, country, first, appointmentTtypeId, meetingLang)
        this.subUnits = response.data.sub_units
        this.groupedSubUnits = groupBy(this.subUnits, (el) => el.branch_type)
        this.getSubUnitsLoading = false

        if (Object.keys(this.groupedSubUnits).length === 1) {
          let branchObject = Object.values(this.groupedSubUnits)[0]
          this.branchType = branchObject[0].branch_type
          this.selectedBranch = branchObject[0]
        } else if (Object.keys(this.groupedSubUnits).length !== 0) {
          Object.values(this.groupedSubUnits).some((el) => {
            if (el[0].branch_type == 'video' && this.appCountry !== 'LV') {
              this.branchType = el[0].branch_type
              this.selectedBranch = el[0]
              return true
            } else if (this.appCountry == 'LV') {
              this.branchType = ''
            } else {
              this.branchType = el[0].branch_type
            }
          })
        }
      } catch (error) {
        this.fail = error
        this.getSubUnitsLoading = false
      }
    },

    async getAvailableDates({ unitId, groupValue, appointmentId, lang, country, meetingLang, childrenCount }) {
      this.fail = false
      this.getAvailableDatesLoading = true
      try {
        const response = await api.getAvailableDates(
          unitId,
          groupValue,
          appointmentId,
          lang,
          country,
          meetingLang,
          childrenCount
        )
        if (!response.data.success) return

        this.availableDates.dates = response.data.available_dates
        this.meetingDuration = response.data.duration
        this.getAvailableDatesLoading = false

        // Disabled dates logic
        if (this.availableDates && this.availableDates.dates) {
          this.disabledDates.to = new Date(moment(this.availableDates.dates[0]))
          this.disabledDates.from = new Date(this.availableDates.dates[this.availableDates.dates.length - 1])
        }
        let start = new Date(moment(this.availableDates.dates[0]))
        let end = new Date(this.availableDates.dates[this.availableDates.dates.length - 1])
        let loop = new Date(start)
        let disabledDates = []

        while (loop < end) {
          let newDate = loop.setDate(loop.getDate() + 1)
          let formattedDate = moment(newDate).format('YYYY-MM-DD')
          if (!this.availableDates.dates.includes(formattedDate)) {
            disabledDates.push(formattedDate)
          }
          loop = new Date(newDate)
        }
        this.disabledDates.dates = disabledDates
      } catch (error) {
        this.fail = error
        this.getAvailableDatesLoading = false
      }
    },

    async getTimes({ unitId, groupValue, appointmentId, date, lang, country, meetingLang, dateTo, childrenCount }) {
      this.fail = false
      this.getTimesLoading = true
      try {
        const response = await api.getTimes(
          unitId,
          groupValue,
          appointmentId,
          date,
          lang,
          country,
          meetingLang,
          dateTo,
          childrenCount
        )
        if (!response.data.success) return

        this.times = response.data.times
        this.getTimesLoading = false
      } catch (error) {
        this.fail = error
        this.getTimesLoading = false
      }
    },

    async updateAppointment(appointmentDataObject) {
      this.fail = false
      this.appointmentDataLoading = true
      try {
        const response = await api.updateAppointment(appointmentDataObject)
        this.appointmentData = response.data.appointment
        this.appointmentDataLoading = false

        const keys = ['service', 'calendar_url', 'date']
        if (!response.data.success || keys.some((key) => !(key in this.appointmentData))) {
          this.missingAppointmentKey = true
          this.fail = true
          this.errorMessage = response.data.error
          return
        }
        router.push('/confirmed')
      } catch (error) {
        this.fail = error
        this.appointmentDataLoading = false
      }
    },

    async cancelAppointment({ appointmentId, processId }) {
      this.fail = false
      this.cancelAppointmentLoading = true
      try {
        const response = await api.cancelAppointment(appointmentId, processId)
        this.cancelAppointment = response.data
        this.cancelAppointmentLoading = false
        router.push('/cancel-appointment-confirmed')
      } catch (error) {
        this.fail = error
        this.cancelAppointmentLoading = false
      }
    },
    async getAppointmentDetails({ appointmentId, processId, pageLang, pageCountry }) {
      this.cancelAppointmentDataLoading = true
      try {
        const response = await api.getAppointmentDetails(appointmentId, processId, pageLang, pageCountry)
        if (!response.data.success) {
          this.cancelAppointmentDataLoading = false
          this.fail = response.data.error
          return
        }
        var timeStamp = response.data.appointment.date
        let date = setTimeZone(timeStamp, this.pageCountry)
        response.data.appointment.date = date
        this.cancelAppointmentData = response.data

        this.cancelAppointmentDataLoading = false
        router.push('/cancel')
      } catch (error) {
        this.cancelAppointmentDataLoading = false
        this.fail = error
      }
    },
    async reserveAppointment(appointmentDataObject) {
      try {
        await api.reserveAppointment(appointmentDataObject)
        return true
      } catch (error) {
        return true
      }
    },
    async rescheduleAppointment(appointmentDataObject) {
      this.appointmentDataLoading = true
      this.fail = false
      try {
        const response = await api.rescheduleAppointment(appointmentDataObject)
        this.appointmentData = response.data.appointment
        this.appointmentDataLoading = false
        this.fail = false

        const keys = ['service', 'calendar_url', 'date']

        if (!response.data.success) {
          this.fail = true
          this.errorMessage = response.data.error
          return
        }

        for (let i = 0; i < keys.length; i++) {
          if (!(keys[i] in this.appointmentData)) {
            this.missingAppointmentKey = true
            break
          }
        }

        if (!this.missingAppointmentKey) router.push('/confirmed')
      } catch (error) {
        this.fail = error
        this.appointmentDataLoading = false
      }
    },
  },
})
